import React, { useState } from 'react';
import { ChatBubbleLeftRightIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'bot',
      message: 'Hello! Welcome to CargoHaul. How can I help you today?'
    },
    {
      type: 'bot',
      message: 'Here are some topics I can help you with:',
      isTopics: true,
      topics: [
        'Track my shipment',
        'Shipping rates & services',
        'Delivery times',
        'Contact support',
        'Calculate shipping cost'
      ]
    }
  ]);

  const handleTopicClick = (topic) => {
    setMessage(topic);
    handleSendMessage(new Event('submit'), topic);
  };

  const handleSendMessage = (e, topicMessage = null) => {
    e.preventDefault();
    const messageToSend = topicMessage || message;
    if (!messageToSend.trim()) return;

    // Add user message to chat
    setChatHistory(prev => [...prev, { type: 'user', message: messageToSend }]);

    // Simulate bot response
    setTimeout(() => {
      const botResponse = getBotResponse(messageToSend);
      setChatHistory(prev => [...prev, { type: 'bot', message: botResponse }]);
    }, 1000);

    setMessage('');
  };

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('tracking') || lowerMessage.includes('track')) {
      return 'You can track your shipment by entering the tracking number on our tracking page. Would you like me to help you with that?';
    }
    
    if (lowerMessage.includes('shipping') || lowerMessage.includes('delivery')) {
      return 'We offer various shipping services including Standard, Express, and Priority delivery. What type of shipment are you looking for?';
    }
    
    if (lowerMessage.includes('price') || lowerMessage.includes('cost')) {
      return 'Our pricing depends on the package size, weight, and delivery service. Would you like me to help you calculate the cost?';
    }
    
    if (lowerMessage.includes('contact') || lowerMessage.includes('support')) {
      return 'You can reach our support team at support@cargohaulco.org, call us at +27 74 258 6662, or WhatsApp us at +27 62 367 6161. We\'re available 24/7!';
    }
    
    if (lowerMessage.includes('time') || lowerMessage.includes('delivery time')) {
      return 'Delivery times vary based on the service selected. Standard delivery takes 3-5 business days, Express takes 1-2 days, and Priority is same-day delivery.';
    }
    
    return 'I\'m here to help with any questions about our shipping services, tracking, or support. What would you like to know?';
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <ChatBubbleLeftRightIcon className="w-6 h-6" />
        </button>
      ) : (
        <div className="bg-white rounded-2xl shadow-xl w-80 h-96 flex flex-col">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 rounded-t-2xl flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              <span className="font-semibold">CargoHaul Support</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatHistory.map((chat, index) => (
              <div
                key={index}
                className={`flex ${chat.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    chat.type === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {chat.message}
                  {chat.isTopics && (
                    <div className="mt-3 space-y-2">
                      {chat.topics.map((topic, topicIndex) => (
                        <button
                          key={topicIndex}
                          onClick={() => handleTopicClick(topic)}
                          className="w-full text-left px-3 py-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm"
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500"
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-2 rounded-lg hover:opacity-90 transition-opacity"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBubble; 